/**
 * The scoring-program endpoints, from the browser.
 *
 * These run in the client rather than through a server action because the
 * editor is interactive: an expression previews while you type, a node saves as
 * you leave it, a test runs on a click. A round trip through a server action per
 * keystroke would make all three feel broken.
 */

import { apiFetch } from "@/lib/api/auth";
import { getAdminApiBaseUrl } from "@/lib/api/config";
import type { CanvasGraph, NodeKind, ScoringProgram } from "@/lib/scoring/types";

/**
 * Straight to the admin service.
 *
 * This used to be the same-origin path `/api/scoring`, forwarded by a route
 * handler in this app, because `ADMIN_API_BASE_URL` was server-only and the
 * admin host was not allowed to reach the browser. The UI is a static bundle
 * now: there is no server to forward anything, so the browser calls the admin
 * service itself and the route handler is gone.
 *
 * The handler's whole job was `${base}/scoring/${path}`, which is what this
 * builds — the requests on the wire are unchanged apart from their host.
 */
const ROOT = `${getAdminApiBaseUrl()}/scoring`;
const FLOWS_ROOT = `${getAdminApiBaseUrl()}/flows`;

export interface ProgramRow {
  id: string;
  /** Null for a workflow no sport owns: Results, Games operations. */
  sportCode: string | null;
  /** The workflow this version belongs to, e.g. "games". */
  workflow?: string | null;
  workflowName?: string | null;
  version: number;
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  checksum: string | null;
  /** What the author calls it; null shows as "Version N". */
  name: string | null;
  /** Fixture kinds or configs this program scores. Empty = the whole sport. */
  scope: string[];
  commands: number;
  triggers: number;
}

export interface ProgramDetail {
  id: string;
  /** Null for a workflow no sport owns: Results, Games operations. */
  sportCode: string | null;
  /** The workflow this version belongs to, e.g. "games". */
  workflow?: string | null;
  workflowName?: string | null;
  version: number;
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  checksum: string | null;
  name: string | null;
  scope: string[];
  document: ScoringProgram;
}

export interface Violation {
  nodeId: string | null;
  message: string;
}

export interface Difference {
  fixture: string;
  label: string;
  where: string;
}

export interface Report {
  ok: boolean;
  violations: Violation[];
  differences: Difference[];
  testsRun: number;
  goldensReplayed: number;
}

export interface ExprPreview {
  ok: boolean;
  value: unknown;
  truthy: boolean;
  error: string | null;
  reads: string[];
}

export interface NodeRun {
  ok: boolean;
  value: unknown;
  error: string | null;
  fuelUsed: number;
  wallMs: number;
}

export class ScoringApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ScoringApiError";
    this.status = status;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  // Paths are relative to the scoring root; a full URL (the flows endpoints)
  // passes through untouched.
  const url = path.startsWith("http") ? path : `${ROOT}${path}`;
  const response = await apiFetch(url, {
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!response.ok) {
    const detail =
      body && typeof body === "object" && "detail" in body
        ? String((body as { detail: unknown }).detail)
        : `${init?.method ?? "GET"} ${path} failed: ${response.status}`;
    throw new ScoringApiError(detail, response.status);
  }
  return body as T;
}

const base = "/programs";

/** Every workflow's versions: a sport's scoring, Results and Games operations
 *  alike. Narrow by `sport`, or by `workflow` code. */
export const listPrograms = (
  query: { sport?: string; workflow?: string } = {},
): Promise<ProgramRow[]> => {
  const params = new URLSearchParams();
  if (query.sport) params.set("sport", query.sport);
  if (query.workflow) params.set("workflow", query.workflow);
  return call(params.size > 0 ? `${base}?${params}` : base);
};

export const getProgram = (id: string): Promise<ProgramDetail> => call(`${base}/${id}`);

export const createProgram = (body: {
  sportCode: string;
  document?: unknown;
  fromVersion?: string;
  name?: string;
  scope?: string[];
}): Promise<ProgramDetail> => call(base, { method: "POST", body: JSON.stringify(body) });

/** Point a draft at its formats. Empty = the whole sport. The server refuses
 *  a name that is not one of the sport's declared kinds or configs. */
export const setScope = (id: string, scope: string[]): Promise<ProgramDetail> =>
  call(`${base}/${id}/scope`, { method: "PATCH", body: JSON.stringify({ scope }) });

/** Call a workflow what it is — "T20 scoring", not "Version 7". */
export const renameProgram = (id: string, name: string): Promise<ProgramDetail> =>
  call(`${base}/${id}/name`, { method: "PATCH", body: JSON.stringify({ name }) });

/** Throw a draft away. Refused on a published version, which is frozen. */
export const discardProgram = (id: string): Promise<null> =>
  call(`${base}/${id}`, { method: "DELETE" });

export const validateProgram = (id: string, replay = true): Promise<Report> =>
  call(`${base}/${id}/validate?replay=${replay}`, { method: "POST" });

export const publishProgram = (id: string, replay = true): Promise<Report> =>
  call(`${base}/${id}/publish?replay=${replay}`, { method: "POST" });

export const previewExpr = (
  id: string,
  expr: string,
  scope: Record<string, unknown>,
): Promise<ExprPreview> =>
  call(`${base}/${id}/preview-expr`, { method: "POST", body: JSON.stringify({ expr, scope }) });

export const runNode = (
  id: string,
  nodeId: string,
  scope: Record<string, unknown>,
): Promise<NodeRun> =>
  call(`${base}/${id}/test-node`, { method: "POST", body: JSON.stringify({ nodeId, scope }) });

/**
 * Add a node. The server picks a code that nothing else is using and fills in
 * the defaults that kind cannot do without, so what comes back is fillable
 * rather than broken.
 *
 * `appliesTo` carries the canvas lens: dropped while looking at T20, the node
 * is T20's.
 */
export const createNode = (
  id: string,
  body: {
    kind: NodeKind;
    code?: string;
    label?: string;
    ui?: { x: number; y: number };
    appliesTo?: string[];
    /** Made from the Components pane, to be attached to commands afterwards. */
    component?: boolean;
  },
): Promise<ProgramDetail> =>
  call(`${base}/${id}/nodes`, { method: "POST", body: JSON.stringify(body) });

/** Remove a node, and every reference to it — see the endpoint's note on why
 *  the delete cleans up after itself. */
export const deleteNode = (id: string, nodeId: string): Promise<ProgramDetail> =>
  call(`${base}/${id}/nodes/${encodeURIComponent(nodeId)}`, { method: "DELETE" });

/**
 * Save one node. `rev` is what the editor last saw; a mismatch comes back 409
 * with the document as it now stands, so the caller can show what changed
 * rather than silently winning or silently losing.
 */
export async function saveNode(
  id: string,
  nodeId: string,
  node: Record<string, unknown>,
  rev: number,
): Promise<{ conflict: boolean; detail: ProgramDetail }> {
  const response = await apiFetch(`${ROOT}${base}/${id}/nodes/${nodeId}`, {
    method: "PATCH",
    headers: { "content-type": "application/json", "If-Match": String(rev) },
    body: JSON.stringify({ node }),
  });
  const detail = (await response.json()) as ProgramDetail;
  if (response.status === 409) return { conflict: true, detail };
  if (!response.ok) throw new ScoringApiError("could not save the node", response.status);
  return { conflict: false, detail };
}

/**
 * What a refused response says, in the two shapes a refusal arrives in.
 *
 * A rule refusal is a sentence — `{"detail": "nothing connects to a command"}`.
 * A body that does not fit the schema comes back as FastAPI's *list* of field
 * errors, and reading only the string form turned exactly the case worth
 * diagnosing into "something went wrong".
 */
async function refusalIn(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === "string") return body.detail;
    if (Array.isArray(body.detail)) {
      const first = body.detail[0] as { loc?: unknown[]; msg?: string } | undefined;
      if (first?.msg) {
        const where = Array.isArray(first.loc) ? first.loc.join(".") : "";
        return where ? `${where}: ${first.msg}` : first.msg;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Store the picture. One request per gesture, and the whole graph every time.
 *
 * The server writes the engine's fields to match, so this is the only call the
 * canvas needs — a card moved, a card added or taken off, a line drawn or cut
 * are all the same request. A refusal comes back as its own sentence, because
 * the server checks the same rules the canvas does and a disagreement between
 * them is worth reading rather than swallowing.
 */
export async function saveCanvas(id: string, graph: CanvasGraph): Promise<ProgramDetail> {
  const response = await apiFetch(`${ROOT}${base}/${id}/canvas`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(graph),
  });
  if (!response.ok) {
    throw new ScoringApiError(
      (await refusalIn(response)) ?? "the canvas could not be saved",
      response.status,
    );
  }
  return (await response.json()) as ProgramDetail;
}

/* ---------------------------------------------------------------- reference */

export interface ReferenceStat {
  key: string;
  valueType: string;
  unit: string | null;
  description: string | null;
  targets: string[];
  appliesTo: string[];
  rows: string;
  example: unknown;
}

export interface ReferenceSlot {
  path: string;
  code: string;
}

/** One readable field, named the way an author types it. `name` may be dotted
 *  (`venue.city`) — what matters is the string they are about to write. */
export interface ReferenceField {
  name: string;
  type: string;
  what: string;
  example: string | null;
  /** Commands only — whether the scorer must send it. */
  required?: boolean | null;
}

/** One top-level name in scope, spelled both ways it is written: an expression
 *  reads `derived.x`, a function reads `ctx.derived.x`. */
export interface ReferenceRoot {
  name: string;
  inExpression: string;
  inFunction: string;
  type: string;
  what: string;
  note: string;
  fields: ReferenceField[];
}

/** `empty` is the distinction that matters: from inside a function, "not in
 *  scope" and "in scope and always empty" look identical, and only one of them
 *  is worth debugging. */
export interface ReferenceVisibility {
  root: string;
  present: boolean;
  empty: boolean;
  note: string;
}

export interface ReferenceNodeKind {
  kind: string;
  label: string;
  signature: string;
  returns: string;
  writes: string;
  scope: ReferenceVisibility[];
}

export interface ReferenceLanguage {
  operators: { name: string; operators: { symbol: string; what: string }[] }[];
  functions: ReferenceField[];
  nullRules: string[];
  examples: { what: string; code: string }[];
}

export interface ReferenceFunctions {
  rules: string[];
  limits: { what: string; value: string }[];
  refusals: { what: string; why: string }[];
}

export interface ReferenceRowShape {
  target: string;
  label: string;
  what: string;
  rule: string;
  fields: ReferenceField[];
  example: string;
}

export interface ReferenceCommand {
  code: string;
  label: string;
  fields: ReferenceField[];
}

export interface Reference {
  sportCode: string;
  fixtureKinds: { code: string; label: string }[];
  configs: { code: string; label: string; appliesTo: string[]; values: Record<string, unknown> }[];
  masters: { code: string; label: string; fields: string[] }[];
  roles: string[];
  stats: ReferenceStat[];
  derived: ReferenceSlot[];
  readable: ReferenceSlot[];
  roots: ReferenceRoot[];
  kinds: ReferenceNodeKind[];
  language: ReferenceLanguage;
  functions: ReferenceFunctions;
  rowShapes: ReferenceRowShape[];
  commands: ReferenceCommand[];
}

/** Everything an author's function may touch — the publish gate's own data,
 *  made readable. */
export const getReference = (id: string): Promise<Reference> => call(`${base}/${id}/reference`);

export interface ComponentSource {
  key: string;
  sportCode: string;
  kind: string;
  module: string;
  line: number;
  source: string;
  inheritedFrom: string | null;
}

/** The Python source of one flows component — what the read-only viewer shows
 *  where the editable function used to be. */
export const componentSource = (key: string): Promise<ComponentSource> =>
  call(`${FLOWS_ROOT}/components/${encodeURIComponent(key)}/source`);
